import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CpuChipIcon,
  ChartBarIcon,
  CogIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const Services: React.FC = () => {
  const services = [
    {
      icon: ChartBarIcon,
      title: 'Data & Analytics',
      description: 'Unlock the power of your data to extract insights, drive smarter decisions, optimize operations, and uncover revenue opportunities',
      features: [
        'Data quality assessment',
        'Data pipeline optimization',
        'Data governance frameworks',
        'Data warehouse design',
        'Data visualization tools'
      ]
    },
    {
      icon: CogIcon,
      title: 'AI Products & Services',
      description: 'Build end-to-end AI solutions or integrate existing products with AI systems alongside ongoing support and optimization.',
      features: [
        'Custom AI solution',
        'End-to-end design/development',
        'AI systems integration',
        'Scalable AI APIs',
        'Model development and training'
      ]
    },
    {
      icon: CpuChipIcon,
      title: 'AI Strategy Consulting',
      description: 'Craft a winning AI strategy that aligns innovation with business objectives, reduces risk, and delivers measurable ROI',
      features: [
        'AI readiness assessment',
        'Business impact analysis',
      'ROI-driven roadmap',
    'Use case prioritization',
    'Change management planning',
      ]
    },
    {
      icon: RocketLaunchIcon,
      title: 'Generative AI',
      description: 'Create personalized experiences, increased productivity, enhanced creativity and new possibilities with LLMs and other AI models.',
      features: [
        'Interactive AI-based apps',
        'AI-powered content generation',
        'Synthetic data generation',
        'Personalized user experiences',
        'Documentation generation',
      ]
    },
    {
      icon: ShieldCheckIcon,
      title: 'AI Agents',
      description: 'Automate to create personalized user experience with  AI agents that perform complex tasks, streamline workflows, and reduce costs.',
      features: [
       'Autonomous task execution',
    'Process automation bots',
    'Intelligent research assistants',
    'Multi-agent collaboration',
    'Adaptive learning systems'
      ]
    },
    {
      icon: UserGroupIcon,
      title: 'AI Chatbots',
      description: 'Deliver smarter, faster, and more personalized customer interactions with AI chatbots that boost engagement and reduce support costs.',
      features: [
        'Customer support bots',
    'Sales conversion chatbots',
    'Multilingual conversation AI',
    'Omni-channel integration',
    'Context-aware responses',
      ]
    },
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Discovery & Assessment',
      description: 'We analyze your current state, goals, and challenges to understand your AI needs.',
    },
    {
      number: '02',
      title: 'Strategy Development',
      description: 'Create a comprehensive AI strategy with clear objectives, timelines, and success metrics.',
    },
    {
      number: '03',
      title: 'Solution Design',
      description: 'Design custom AI solutions tailored to your specific business requirements.',
    },
    {
      number: '04',
      title: 'Implementation',
      description: 'Execute the solution with careful attention to quality, security, and performance.',
    },
    {
      number: '05',
      title: 'Optimization',
      description: 'Continuously monitor, improve, and optimize your AI solutions for maximum impact.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 section-padding">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-6">
              AI Consulting Services
            </h1>
            <p className="text-md text-secondary-600 max-w-3xl mx-auto mb-8">
              Comprehensive AI consulting services designed to transform your business operations, 
              enhance decision-making, and drive innovation through cutting-edge technologies.
            </p>
            <Link to="/contact" className="btn-primary">
              Get Free Consultation
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
              Our Services
            </h2>
            <p className="text-md text-secondary-600 max-w-3xl mx-auto">
              From strategy to implementation, we provide end-to-end AI solutions and legacy integration for small to mid-size businesses.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-secondary-200 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-secondary-600 mb-4">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-secondary-600">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-secondary-200 pt-4">
                  <Link
                    to="/contact"
                    className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                  >
                    Get Quote →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-secondary-50 section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
              Our Process
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              A well-defined methodology that ensures maximum business value and success.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-lg">{step.number}</span>
                </div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-secondary-600 text-sm">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 section-padding">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
              Ready to get started?
            </h2>
            <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
              Schedule a free consultation to discuss your AI needs and discover how we can help transform your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary text-center bg-white text-primary-600 hover:bg-secondary-100 transition-colors duration-200">
              
                Schedule a Consultation
              </Link>
             </div>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;

